import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { showToast, toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import type { IAlert } from 'shared/ui/Alert/types'
import { addContactIdsToUrlParams, addInboxIdToUrlParams } from 'shared/lib/createDeepLink'
import { logger } from 'shared/lib'
import type { Contact } from 'entities/Contacts/model/Contact'
import type {
  IParamsConversationCreate,
  IResponseConversation,
} from 'entities/Conversation/api/types'
import type { IParamsCreateMessage } from 'entities/Message'
import { type IParamsSendFromSmartOption } from 'entities/SendFrom'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { Conversation } from 'entities/Conversation'
import { ConversationGroupError } from 'entities/Conversation/model/ConversationGroupError'
import type { IScheduledData } from 'widgets/ConversationSchedule/store/conversationScheduleStore'
import type { ConversationMessagesStore } from 'widgets/ConversationMessages'
import type { IConversationNewSearchStoreConfig } from 'widgets/ConversationNew/store/types'
import { SendFromStore } from 'widgets/SendFrom'
import { type ISendFromStoreProps } from 'widgets/SendFrom/store/SendFromStore'

export class ConversationNewSearchStore {
  private _search = ''
  private _searchFocusTrigger = nanoid()
  private _openDropDown = false
  private _itemsMap: Map<number, Contact> = new Map()
  private _visibleUI = false
  private _disposeItems: IReactionDisposer | null = null
  private _disposeInbox: IReactionDisposer | null = null
  private _disposeDropDownOpen: IReactionDisposer | null = null
  private _conversationMessagesStore: ConversationMessagesStore | null = null
  private _automaticallyCreateNewContact = false
  private _alert: IAlert | null = null
  private _firstEditMode = false
  private _config: IConversationNewSearchStoreConfig | null = null
  private readonly _sendFromStore: SendFromStore

  constructor(private _entities: IEntitiesStore, sendFromStoreProps?: ISendFromStoreProps) {
    this._sendFromStore = new SendFromStore(this._entities, {
      autoSelectInbox: true,
      personalInboxesOnly: true,
      onChangeInboxId: (id) => {
        if (!id) return

        this.changeCurrentInbox(Number(id))
      },
      ...sendFromStoreProps,
    })
    makeAutoObservable(this)
  }

  loadMessages = () => {
    if (!this._conversationMessagesStore) return

    this._conversationMessagesStore.resetMessage()

    if (!this.activeInboxId && !this.items.length) return

    if (this._conversationMessagesStore.conversationId) {
      this._conversationMessagesStore.loadMessages()
    } else {
      this._conversationMessagesStore.loadMessagesContact({
        before: this._conversationMessagesStore.loadingBeforeId,
      })
    }
  }

  setConfig = (config: IConversationNewSearchStoreConfig) => {
    this._config = {
      ...this._config,
      ...config,
    }
    this._conversationMessagesStore = config.conversationMessagesStore || null
  }

  init = () => {
    if (this._conversationMessagesStore) {
      this._conversationMessagesStore.setSendFromStore(this._sendFromStore)
    }
  }

  reset = () => {
    this._search = ''
    this._openDropDown = false
    this._itemsMap.clear()
    this._automaticallyCreateNewContact = false
    this._conversationMessagesStore?.setSendFromStore(null)
  }

  initInbox = (inbox_id?: number) => {
    if (!this._config?.autoSelectInbox) return

    const inboxId = inbox_id || Number(this._entities.inboxesStore.currentInboxId)
    this._conversationMessagesStore?.setInboxId(inboxId)
    const conversation = this._entities.conversationStore.getItem(0)

    if (conversation) {
      conversation.inbox_id = inboxId
      this._entities.conversationStore.updateItem(conversation.origin)
    }

    if (inboxId) {
      this._sendFromStore.setSelectItem({
        inbox_id: inboxId,
      })
    }
  }

  setVisibleUI = (status: boolean) => {
    this._visibleUI = status
  }

  reactionItems = () => {
    this._disposeItems?.()
    this._disposeItems = reaction(
      () => this.items.length,
      (length) => {
        if (!length) {
          this._conversationMessagesStore?.reset()
        }

        this.handleUpdateContacts()
        if (this.hasConversationPage) {
          addContactIdsToUrlParams(this.items.map((contact) => contact.id))
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  handleUpdateInbox = () => {
    const inbox = this._entities.inboxesStore.getItem(this.activeInboxId)
    const conversation = this._entities.conversationStore.getItem(0)

    if (inbox?.type === 'inbox' && conversation) {
      const response: IResponseConversation = {
        ...conversation.origin,
        inbox_id: inbox.id,
      }

      this._entities.conversationStore.addItem(response)
    }
  }

  handleUpdateContacts = () => {
    const conversation = this._entities.conversationStore.getItem(0)
    if (this.items.length) {
      const contactId = this.items[0].id
      const participants = this.items
        .filter((contact, index) => index !== 0)
        .map((contact) => contact.origin)

      if (conversation) {
        const response: IResponseConversation = {
          ...conversation.origin,
          contact_id: contactId,
          participants: participants,
          is_group: Boolean(this.items.length > 1),
        }

        this._entities.conversationStore.addItem(response)
      }
    } else {
      if (conversation) {
        const contact = this._entities.contactsStore.createEmptyContact()
        const response: IResponseConversation = {
          ...conversation.origin,
          contact_id: 0,
          participants: [],
          is_group: false,
        }

        this._entities.contactsStore.addItem(contact)
        this._entities.conversationStore.addItem(response)
      }
    }
  }

  handleInputSearch = (value: string) => {
    this._search = value
    this._automaticallyCreateNewContact = false
  }

  reactionDropDownOpen = () => {
    this._disposeDropDownOpen?.()
    this._disposeDropDownOpen = reaction(
      () => this._search.length,
      () => {
        this.handleOpenDropDown()
      }
    )
  }

  handleAutomaticallyCreateNewContact = () => {
    this._automaticallyCreateNewContact = true
  }

  handleCloseDropDown = () => {
    this._openDropDown = false
  }

  handleOpenDropDown = () => {
    this._openDropDown = Boolean(this._search.length)
  }

  handleSetActiveNumber = (
    inbox_id: number,
    number_id?: number,
    smart_option?: IParamsSendFromSmartOption
  ) => {
    this._sendFromStore.setSelectItem({ inbox_id, number_id, smart_option })
  }

  handleAddItem = async (contact: Contact) => {
    const currentInbox = this._entities.inboxesStore.currentInbox

    if (currentInbox && currentInbox.type === 'inbox') {
      const numbers = currentInbox?.numberIds.map((id) => this._entities.numbersStore.getItem(id))
      const numbersTollFree = numbers.filter((item) => item?.isTollFree)
      const isOnlyAllTollFreeNumbers = numbers.length === numbersTollFree.length
      const isAircall = !!this._entities.conversationStore.conversationInboxNumber?.is_aircall

      if (
        (isAircall || isOnlyAllTollFreeNumbers || currentInbox.isSinch || currentInbox.isRCS) &&
        this.items.length === 1
      ) {
        const type = isAircall ? 'Aircall' : 'Toll-Free'

        const message = currentInbox.isRCS
          ? 'Group messaging is not supported for RCS inbox.'
          : currentInbox.isSinch
          ? 'Group messaging is temporarily unavailable.'
          : `Group messaging is not supported for ${type} numbers. Please use a local number to proceed.`

        showToast({
          type: 'error',
          title: message,
        })

        return
      }

      if (this.items.length >= 9) {
        showToast({
          type: 'error',
          title: 'Only 9 people can be in a group message.',
        })

        return
      }
    }

    this._itemsMap.set(contact.id, contact)
    this._search = ''
    this._searchFocusTrigger = nanoid()
    this._automaticallyCreateNewContact = false
  }

  handleDeleteItem = (item: Contact) => {
    this._itemsMap.delete(item.id)
  }

  handleCreateConversation = async (
    scheduledData?: IScheduledData,
    isDoubleOptInButton?: boolean,
    message?: IParamsCreateMessage,
    withUndo?: boolean,
    onSendSuccess?: () => void
  ) => {
    if (!this.items.length) return
    if (!this._conversationMessagesStore) return

    const messageFieldStore = this._conversationMessagesStore.messageFieldStore

    try {
      const contact = this.items[0]
      const contactId = contact.id
      const participantsIds = this.items.filter((item, index) => index !== 0).map((item) => item.id)

      if (!this.activeInboxId) return

      const params: IParamsConversationCreate = {
        contact_id: contactId,
        participants: participantsIds,
        team_id: this.activeInboxId,
      }

      messageFieldStore.setLoading(true)

      if (this.activeNumberId) {
        params.number_id = this.activeNumberId
      }

      if (this.smartOption) {
        params.smart_option = this.smartOption
      }

      let conversation: Conversation | null = null
      const data = await this._entities.conversationStore.createConversation(params)

      if (this._entities.inboxesStore.currentInboxTypeId !== this.activeInboxId) {
        await this._entities.inboxesStore.handleUpdateTeamInbox(this.activeInboxId)
      }

      if (data instanceof Conversation) {
        conversation = data
      }

      if (data instanceof ConversationGroupError) {
        conversation = await data.showModal()
      }

      if (conversation) {
        if (!isDoubleOptInButton) {
          if (messageFieldStore.promiseAttachmentLoading) {
            await messageFieldStore.promiseAttachmentLoading
          }

          const mergedMessage = {
            ...messageFieldStore?.messageRequestData,
            ...message,
            ...scheduledData,
          }

          if (withUndo) {
            this.showUndoProcess(mergedMessage, conversation.id, onSendSuccess)
          } else {
            await this._conversationMessagesStore.sendMessage({
              message: mergedMessage,
              conversationId: conversation.id,
              onSendSuccess,
            })
          }
          await messageFieldStore.reset()
        }

        uiStore.changeRoute({
          path: `/conversations/${conversation.id}`,
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      messageFieldStore.setLoading(false)
    }
  }

  showUndoProcess = (
    message: IParamsCreateMessage,
    conversationId: number,
    onSendSuccess?: () => void
  ) => {
    const id = parseInt(nanoid().replace(/\D/g, ''), 10)
    const toastId = String(id)
    const timer = 5000

    const timeoutId = setTimeout(() => {
      this._conversationMessagesStore?.sendMessage({
        message,
        conversationId,
        onSendSuccess,
      })

      toastStore.remove(toastId)
    }, timer)

    toastStore.add({
      type: 'info',
      id: toastId,
      title: 'Sending in',
      withCountdown: true,
      timer,
      action: {
        text: 'Undo',
        onAction: () => {
          toastStore.remove(toastId)
          clearTimeout(timeoutId)
        },
      },
      secondAction: {
        text: 'Instant send',
        onAction: () => {
          this._conversationMessagesStore?.sendMessage({
            message,
            conversationId,
            onSendSuccess,
          })
          toastStore.remove(toastId)
          clearTimeout(timeoutId)
        },
      },
    })
  }

  changeCurrentInbox = (id: number) => {
    const inbox = this._entities.inboxesStore.getItemShared(id)

    if (inbox) {
      this.handleUpdateInbox()
      this._config?.changeInbox?.(inbox)
      if (this.hasConversationPage) addInboxIdToUrlParams(inbox.id)
    }
  }

  reactionInbox = () => {
    this._disposeInbox?.()
    this._disposeInbox = reaction(
      () => this._entities.inboxesStore.currentInboxId,
      () => {
        if (
          !this._entities.conversationStore.isNewConversation &&
          !this._entities.conversationStore.conversationOpenStore.loading &&
          this._visibleUI
        ) {
          this._entities.conversationStore.conversationOpenStore.handleCloseNewConversation()
        }
      }
    )
  }

  clearReactions = () => {
    this._disposeItems?.()
    this._disposeInbox?.()
    this._disposeDropDownOpen?.()
  }

  checkError = () => {
    return this._sendFromStore.checkError()
  }

  get sendFromStore() {
    return this._sendFromStore
  }

  get search() {
    return this._search
  }

  get activeInboxId() {
    return this._sendFromStore.inbox_id
  }

  get activeNumberId() {
    return this._sendFromStore.number_id
  }

  get smartOption() {
    return this._sendFromStore.smart_option
  }

  get searchFocusTrigger() {
    return this._searchFocusTrigger
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get openDropDown() {
    return this._openDropDown
  }

  get automaticallyCreateNewContact() {
    return this._automaticallyCreateNewContact
  }

  get alert() {
    return this._alert
  }

  setAlert = (item: IAlert) => {
    this._alert = item
    this._config?.onChangeAlert?.(item)
  }

  resetAlert = () => {
    this._alert = null
    this._config?.onChangeAlert?.(null)
  }

  setIsFirstEditMode = (condition: boolean) => {
    this._firstEditMode = condition
  }

  get isFirstEditMode() {
    return this._firstEditMode
  }

  get readOnly() {
    return this._config?.readOnly
  }

  get headerActions() {
    return this._config?.headerActions
  }

  get hasConversationPage() {
    return uiStore.pathName.includes('conversations')
  }

  get config() {
    return this._config
  }
}
